import Vue from 'vue';
import axios from 'axios';
import { ethers } from 'ethers';

/**
 * 数据存证合约调用服务
 * @param {Object} param 合约调用参数
 * @param {string} contractAddress 合约地址
 * @param {string} rpcUrl RPC接口地址
 * @param {Array} contractAbi 可选的合约ABI数组
 * @returns {Promise} 合约调用结果
 */
const dataEvidenceApi = function (param, contractAddress, rpcUrl, contractAbi = null) {
  // 合约接口
  let contractInterface = null;
  if (contractAbi) {
    contractInterface = new ethers.utils.Interface(contractAbi);
  }

  /**
   * 通过数据哈希获取存证数据
   * @param {string} dataHash 数据哈希
   * @returns {Promise<Object>} 存证数据对象
   */
  const getEvidenceByDataHash = async (dataHash) => {
    try {
      // 确保dataHash是bytes32格式
      if (!dataHash.startsWith('0x')) {
        dataHash = '0x' + dataHash;
      }
      
      // 准备数据
      let data;
      
      // 如果有合约ABI接口，使用ABI编码
      if (contractInterface) {
        data = contractInterface.encodeFunctionData('getEvidenceByDataHash', [dataHash]);
      } else {
        // 否则手动构建调用
        const functionSignature = 'getEvidenceByDataHash(bytes32)';
        const functionHash = ethers.utils.id(functionSignature).slice(0, 10); // 取前4字节
        
        const abiCoder = new ethers.utils.AbiCoder();
        const encodedParams = abiCoder.encode(['bytes32'], [dataHash]);
        data = functionHash + encodedParams.slice(2); // 移除0x前缀
      }

      // 构建调用对象
      const callObject = {
        to: contractAddress,
        data
      };

      // 构建RPC请求
      const rpcRequest = {
        jsonrpc: '2.0',
        id: Date.now(),
        method: 'eth_call',
        params: [
          callObject,
          'latest'
        ]
      };

      // 发送请求
      const config = {
        baseURL: rpcUrl.startsWith('http') ? '' : '/api',
        url: rpcUrl,
        method: 'post',
        headers: {'Content-Type': 'application/json'},
        data: rpcRequest
      };
      
      // 创建axios实例
      const _axios = axios.create(config);
      
      // 请求拦截器
      _axios.interceptors.request.use(
        (config) => {
          return config;
        },
        (error) => {
          return Promise.reject(error);
        }
      );
      
      // 响应拦截器
      _axios.interceptors.response.use(
        (response) => {
          // 检查响应
          if (response.data.error) {
            throw new Error(`RPC Error: ${response.data.error.message}`);
          }
          
          // 获取结果
          const result = response.data.result;
          
          if (!result || result === '0x') {
            throw new Error('找不到对应的存证记录');
          }
          
          // 解码返回数据
          let decodedData;
          if (contractInterface) {
            // 使用ABI解码
            decodedData = contractInterface.decodeFunctionResult('getEvidenceByDataHash', result);
          } else {
            // 手动解码
            const abiCoder = new ethers.utils.AbiCoder();
            decodedData = abiCoder.decode(
              ['bytes32', 'address', 'uint256', 'string', 'string', 'bool', 'uint256', 'bytes32'], 
              result
            );
          }
          
          // 返回结构化对象
          return {
            evidenceId: decodedData.evidenceId || decodedData[0],
            submitter: decodedData.submitter || decodedData[1],
            timestamp: decodedData.timestamp || decodedData[2],
            dataType: decodedData.dataType || decodedData[3],
            metadata: decodedData.metadata || decodedData[4],
            isRevoked: decodedData.isRevoked || decodedData[5],
            blockNumber: Number(decodedData.blockNumber || decodedData[6]),
            blockHash: decodedData.blockHash || decodedData[7]
          };
        },
        (error) => {
          return Promise.reject(error);
        }
      );
      
      return await _axios();
      
    } catch (error) {
      console.error('获取存证数据失败:', error);
      throw error;
    }
  };

  /**
   * 检查数据哈希是否已被用户提交
   * @param {string} dataHash 数据哈希
   * @param {string} submitter 提交者地址
   * @returns {Promise<boolean>} 是否已提交
   */
  const isDataHashSubmitted = async (dataHash, submitter) => {
    // 这里添加实现...
    // 与getEvidenceByDataHash类似，只是调用的合约方法不同
    return false;
  };

  // 根据参数决定调用哪个方法
  if (param && param.method === 'getEvidenceByDataHash') {
    return getEvidenceByDataHash(param.dataHash);
  } else if (param && param.method === 'isDataHashSubmitted') {
    return isDataHashSubmitted(param.dataHash, param.submitter);
  }
  
  // 默认返回一个包含所有方法的对象
  return {
    getEvidenceByDataHash,
    isDataHashSubmitted
  };
};

const Plugin = {};

Plugin.install = function (Vue, options) {
  Object.defineProperties(Vue.prototype, {
    $dataEvidence: {
      get() {
        return dataEvidenceApi;
      },
    },
  });
};

Vue.use(Plugin);

// 导出Vue插件
export default Plugin;

// 同时导出API函数，以便直接导入使用
export { dataEvidenceApi }; 